import { UpdateEventClassEntryInput, UpdateEventEntryInput } from "../../API";
import { OptionGroupsSortedByDiscipline } from "../../components/EventClass/ClassInfoSection";
import { formattedOption, formattedOptionGroup } from "../../components/EventClass/ClassInfoSection";
import { Result } from "../../interfaces/Result";
import { EventClassEntry, EventEntry } from "../../models";
import { deleteEventClassEntry, getEventClassEntriesWithEventClassByEventEntryId, updateEventClassEntry } from "../eventClassEntry/EventClassEntry";
import { createDivisionListForEntry, CreateEntryInterface, createEventClassEntryFromFormattedOptionGroup } from "./CreateEventEntryUtils";
import { updateEventEntry } from "./EventEntry";
import { EventEntryStatusOptions } from "./EventEntryStatus";

export async function updateExistingEventEntry(oldEntry: EventEntry, input: CreateEntryInterface): Promise<Result> {

    const divisionList = input.disciplinesWithSelectedValues ? createDivisionListForEntry(input.disciplinesWithSelectedValues) : [];
        

    // create entry with status = in_progress
    const eventEntryInput: UpdateEventEntryInput = {
        id: oldEntry.id,
        status: input.isIncomplete ? EventEntryStatusOptions.inProgress : EventEntryStatusOptions.complete,
        contactId: input.currentContact?.id || "", 
        riderId: input.currentRider?.id, 
        riderName: input.currentRider?.name, 
        horseId: input.currentHorse?.id, 
        horseName: input.currentHorse?.name, 
        ownerId: input.currentOwner?.id, 
        ownerName: input.currentOwner?.name, 
        trainerId: input.currentTrainer?.id,
        trainerName: input.currentTrainer?.name,
        barnName: input.currentBarn?.name,
        barnId: input.currentBarn?.id,
        secondRiderId: input.currentRider2?.id, 
        secondRiderName: input.currentRider2?.name,
        payerName: input.payerName,
        divisionList:  divisionList,
        statusNote: input.isIncomplete 
            ? "incomplete" 
            : (oldEntry.statusNote === "incomplete" ? null : oldEntry.statusNote)
    };
    const entryResult = await updateEventEntry(eventEntryInput);
    if (entryResult.isSuccess) {

        // Create the classes for this entry
        await updateEventClassEntries(oldEntry, input);

        let result: Result = {isSuccess: true, type: "EventEntry", result: entryResult.result, message: "Successfully created the event entry!"};
        return result;
    } else {
        let result: Result = {isSuccess: false, type: "EventEntry", result: entryResult.result, message: "Could not create the entry. Please contact hello@ringsidepro.com for help."};
        return result;
    }
}


const updateEventClassEntries = async (oldEntry: EventEntry, input: CreateEntryInterface) => {
    // Get the previous EventClassEntry objects for this entry
    let previousEventEntries: EventClassEntry[] = [];
    if (previousEventEntries.length === 0) {
        const eventClassEntriesResult = await getEventClassEntriesWithEventClassByEventEntryId(oldEntry.id);
        if (eventClassEntriesResult.isSuccess) {
            previousEventEntries = eventClassEntriesResult.result;
        }
    }

    // Get the lists of classes that need to be added or removed from this entry's EventClassEntry objects
    const disciplinesWithSelectedValues = input.disciplinesWithSelectedValues;
    if (disciplinesWithSelectedValues && disciplinesWithSelectedValues.length > 0) {
        for (let i = 0; i < disciplinesWithSelectedValues.length; i++) {
            const discipline: OptionGroupsSortedByDiscipline = disciplinesWithSelectedValues[i];
            const optionGroupArray: formattedOptionGroup[] = discipline.optionGroupArray;

            for (let j = 0; j < optionGroupArray.length; j++) {
                const optionGroup = optionGroupArray[j];
                
                // Check if this is a one-off class
                if (optionGroup.class) {

                    // If checked, check if it needs to be added
                    if (optionGroup.class.isChecked) {
                        let alreadyEntered: (EventClassEntry | null) = null;
                        if (previousEventEntries && previousEventEntries.length > 0) {
                            for (let l = 0; l < previousEventEntries.length; l++) {
                                const previousEventClassEntry: (EventClassEntry | null) = previousEventEntries[l];
                                if (previousEventClassEntry) {
                                    if (previousEventClassEntry.eventClass?.id === optionGroup.class.value){
                                        alreadyEntered = previousEventClassEntry;
                                    }
                                }
                            }
                        }
                        if (alreadyEntered) {
                            const formattedOption: formattedOption = optionGroup.class;
                            const updateResult = await updateEventClassEntryFromFormattedOptionGroup(input, oldEntry, formattedOption, alreadyEntered.id);
                            if (!updateResult.isSuccess) {
                                console.error("Could not update the EventClassEntry: ", updateResult);
                            }
                        } else {
                            const formattedOption: formattedOption = optionGroup.class;
                            const createResult = await createEventClassEntryFromFormattedOptionGroup(input, oldEntry, formattedOption);
                            if (!createResult.isSuccess) {
                                console.error("Could not create the EventClassEntry: ", createResult);
                            }
                        }
                    } 
                    
                    // If not checked, check if it needs to be remove
                    else {
                        let alreadyEntered: (EventClassEntry | null) = null;
                        if (previousEventEntries && previousEventEntries.length > 0) {
                            for (let l = 0; l < previousEventEntries.length; l++) {
                                const previousEventClassEntry: (EventClassEntry | null) = previousEventEntries[l];
                                if (previousEventClassEntry) {
                                    if (previousEventClassEntry.eventClass?.id === optionGroup.class.value){
                                        alreadyEntered = previousEventClassEntry;
                                    }
                                }
                            }
                        }
                        if (alreadyEntered) {
                            const deleteResult = await deleteEventClassEntry({id: alreadyEntered.id});
                            if (!deleteResult.isSuccess) {
                                console.error("Could not delete the EventClassEntry: ", deleteResult);
                            }
                        }
                    }
                }

                // Check if this is a division with multiple classes
                else if (optionGroup.classes && optionGroup.classes.length > 0) {
                    for (let k = 0; k < optionGroup.classes.length; k++) {
                        const optionGroupClass = optionGroup.classes[k];
                        if (optionGroupClass.isChecked) {
                            let alreadyEntered: (EventClassEntry | null) = null;
                            if (previousEventEntries && previousEventEntries.length > 0) {
                                for (let l = 0; l < previousEventEntries.length; l++) {
                                    const previousEventClassEntry: (EventClassEntry | null) = previousEventEntries[l];
                                    if (previousEventClassEntry) {
                                        if (previousEventClassEntry.eventClass?.id === optionGroupClass.value){
                                            alreadyEntered = previousEventClassEntry;
                                        }
                                    }
                                }
                            }
                            if (alreadyEntered) {
                                const updateResult = await updateEventClassEntryFromFormattedOptionGroup(input, oldEntry, optionGroupClass, alreadyEntered.id);
                                if (!updateResult.isSuccess) {
                                    console.error("Could not update the EventClassEntry: ", updateResult);
                                }
                            } else {
                                const createResult = await createEventClassEntryFromFormattedOptionGroup(input, oldEntry, optionGroupClass);
                                if (!createResult.isSuccess) {
                                    console.error("Could not create the EventClassEntry: ", createResult);
                                }
                            } 
                        } 

                        // If not checked, check if it needs to be remove
                        else {
                            let alreadyEntered: (EventClassEntry | null) = null;
                            if (previousEventEntries && previousEventEntries.length > 0) {
                                for (let l = 0; l < previousEventEntries.length; l++) {
                                    const previousEventClassEntry: (EventClassEntry | null) = previousEventEntries[l];
                                    if (previousEventClassEntry) {
                                        if (previousEventClassEntry.eventClass?.id === optionGroupClass.value){
                                            alreadyEntered = previousEventClassEntry;
                                        }
                                    }
                                }
                            }
                            if (alreadyEntered) {
                                const deleteResult = await deleteEventClassEntry({id: alreadyEntered.id});
                                if (!deleteResult.isSuccess) {
                                    console.error("Could not delete the EventClassEntry: ", deleteResult);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

export const updateEventClassEntryFromFormattedOptionGroup = async (input: CreateEntryInterface, eventEntry: EventEntry, formattedOption: formattedOption, eventClassEntryId: string) => {
    const eventClassEntryInput: UpdateEventClassEntryInput = {
        id: eventClassEntryId,
        lastUpdatedBy: input.userId,
        riderId: formattedOption.selectedRider?.id || input.currentRider?.id || "",
        status: EventEntryStatusOptions.complete //TO DO: Check if event auto-accepts entries
    };
    const updateResult = await updateEventClassEntry(eventClassEntryInput);
    return updateResult;
}